import { useState, useEffect, useCallback, createContext, useContext, ReactNode } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../config/firebase';
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  getDocs,
  writeBatch,
} from 'firebase/firestore';

export interface FavoriteItem {
  productId: string;
  name: string;
  brand: string;
  price: number;
  imageUrl: string;
  addedAt: number;
}

const FAVORITES_STORAGE_KEY = 'fash_favorites';

function loadLocalFavorites(): FavoriteItem[] {
  try {
    const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveLocalFavorites(items: FavoriteItem[]) {
  localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(items));
}

function useFavorites() {
  const [items, setItems] = useState<FavoriteItem[]>(loadLocalFavorites);
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const favoritesCol = useCallback(
    (uid: string) => collection(db, 'users', uid, 'favorites'),
    []
  );

  // Merge local favorites into Firestore, then load the combined set
  const syncToFirestore = useCallback(
    async (uid: string) => {
      setSyncing(true);
      try {
        const colRef = favoritesCol(uid);
        const snapshot = await getDocs(colRef);

        const remote = new Map<string, FavoriteItem>();
        snapshot.docs.forEach((d) => {
          const data = d.data() as FavoriteItem;
          remote.set(data.productId, data);
        });

        const local = loadLocalFavorites();
        const batch = writeBatch(db);
        let changed = false;

        for (const item of local) {
          if (!remote.has(item.productId)) {
            batch.set(doc(colRef, item.productId), item);
            remote.set(item.productId, item);
            changed = true;
          }
        }

        if (changed) await batch.commit();

        const merged = Array.from(remote.values()).sort(
          (a, b) => b.addedAt - a.addedAt
        );
        setItems(merged);
        saveLocalFavorites(merged);
      } catch (err) {
        console.error('Error syncing favorites:', err);
      } finally {
        setSyncing(false);
      }
    },
    [favoritesCol]
  );

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserId(user.uid);
        await syncToFirestore(user.uid);
      } else {
        setUserId(null);
        setItems(loadLocalFavorites());
      }
      setLoading(false);
    });
    return () => unsub();
  }, [syncToFirestore]);

  const isFavorited = useCallback(
    (productId: string) => items.some((i) => i.productId === productId),
    [items]
  );

  const toggleFavorite = useCallback(
    async (product: Omit<FavoriteItem, 'addedAt'>) => {
      const exists = items.find((i) => i.productId === product.productId);

      if (exists) {
        const next = items.filter((i) => i.productId !== product.productId);
        setItems(next);
        saveLocalFavorites(next);

        if (userId) {
          try {
            await deleteDoc(
              doc(db, 'users', userId, 'favorites', product.productId)
            );
          } catch (err) {
            console.error('Error removing favorite:', err);
          }
        }
      } else {
        const newItem: FavoriteItem = { ...product, addedAt: Date.now() };
        const next = [newItem, ...items];
        setItems(next);
        saveLocalFavorites(next);

        if (userId) {
          try {
            await setDoc(
              doc(db, 'users', userId, 'favorites', product.productId),
              newItem
            );
          } catch (err) {
            console.error('Error adding favorite:', err);
          }
        }
      }
    },
    [items, userId]
  );

  const favoriteCount = items.length;

  return { items, loading, syncing, favoriteCount, isFavorited, toggleFavorite };
}

interface FavoritesContextValue extends ReturnType<typeof useFavorites> {}

const FavoritesContext = createContext<FavoritesContextValue | undefined>(undefined);

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const favorites = useFavorites();
  return (
    <FavoritesContext.Provider value={favorites}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavoritesContext() {
  const ctx = useContext(FavoritesContext);
  if (!ctx) {
    throw new Error('useFavoritesContext must be used within FavoritesProvider');
  }
  return ctx;
}
